const WebSocketState = {
  READY: 0,
  CONNECT: 1,
  INSTANTIATE: 2,
  CONNECTED: 3,
  LISTENING: 4,
  CLOSE: 5,
};

class WebSocket {
  constructor(store, url) {
    this.url = url;
    this.language = store.state.common.user.language;
    this.timeZoneId = store.state.common.user.timeZoneId;
    this.queue = [];
    this.subscription = [];
    this.state = WebSocketState.READY;
  }
  connect() {
    this.state = WebSocketState.CONNECT;
    // Added.by.KWShin.2026.10.27. 닫히는 중인 이전 연결이 보내오는 메시지를 새 구독에 넘기지 않는다.
    // (시스템을 바꿔 다시 연결할 때 이전 연결의 메시지가 같은 키의 새 구독에 섞여 들어가, 인스턴스별 메시지를 모아
    //  한꺼번에 반영하는 차트들의 개수 맞춤이 어긋나 실시간 갱신이 멈췄다)
    // 이전 구독 메시지(대기열)도 새 연결로 보내지 않는다(구독 목록은 아래에서 비운다).
    if (this.instance) {
      this.instance.onmessage = null;
      this.instance.onopen = null;
    }
    this.queue.length = 0;
    this.close();
    this.subscription.length = 0;
    if (this.getState() == window.WebSocket.CLOSING) {
      this.instance.onclose = () => this.instantiate();
    } else {
      this.instantiate();
    }
  }
  instantiate() {
    this.state = WebSocketState.INSTANTIATE;
    const instance = new window.WebSocket(this.url);
    this.instance = instance;
    // Modified.by.KWShin.2026.10.27. 지금 연결의 메시지만 처리한다(위 connect 참고)
    instance.onopen = () => instance === this.instance && this.openListener();
    instance.onmessage = event => instance === this.instance && this.messageListener(event);
    instance.onclose = () => {};
  }
  openListener() {
    this.state = WebSocketState.CONNECTED;
    this.queue.forEach(q => this.instance.send(q));
    this.queue.length = 0;
  }
  /**
   * 서버에서 response가 도착하면,
   * 구독 목록에서 response와 key가 동일한 구독 목록을 찾아낸 후,
   * 아직 buffered response(OWLens View-server API에서의 BulkResponse)를 처리한 상태가 아니면,
   * response의 data를 bufferd function을 호출하면서 넘겨주고,
   * buffered를 false로 바꾼다.
   * 이후에 도착하는 response는 buffered가 false이므로 every function을 호출하면서 넘겨준다.
   * Commented.by.KWShin.2023.06.30.
   */
  messageListener(event) {
    this.state = WebSocketState.LISTENING;
    const response = JSON.parse(event.data);
    const id = this.keyString(response.key);
    this.subscription
      .filter(s => s.id == id)
      .forEach(s => {
        const every = s.handler.every;
        if (s.buffered && typeof s.handler.buffer == 'function') {
          // empty == true (비어있는 데이터) 를 허용할지 여부 (default : true => 비어있는 데이터를 허용함)
          // 만약 비어있는 데이터를 허용하지 않고 filtering 하고자 하면 send message 에서 empty: false 로 지정한다.
          const data = s.empty ? response.data.map(r => r.data || []) : response.data.map(r => (r.data || []).filter(d => !d.empty));
          s.handler.buffer(response.key, data, every);
          s.buffered = false;
        } else {
          const data = s.empty ? response.data : response.empty ? [] : response.data;
          every(response.key, data, response.ctime, response.empty);
        }
      });
  }
  keyString(key) {
    return ['name', 'sstmId', 'instId', 'nodeId'].map(k => key[k] || '').join('_');
  }
  subscribe(args) {
    args.requests.forEach(request => {
      const subscription = {
        id: this.keyString(request.key),
        key: request.key,
        buffered: typeof request.handler.buffer == 'function' ? true : false,
        bufferSize: request.bufferSize,
        empty: request.empty ?? true,
        handler: request.handler,
      };
      this.subscription.push(subscription);
    });
  }
  send(args) {
    this.subscribe(args);
    const message = JSON.stringify(
      args.requests.map(request => ({
        key: request.key,
        fields: request.fields,
        // request에 buffer handler가 지정되지 않으면
        // buffer 데이터가 불필요하다는 의미이다.
        // 서버에서 버퍼 데이터를 다 보내지 말고 버퍼에 있는 마지막 데이터만 보내라는 의미로
        // bufferNotNeeded 라는 정보를 올려보낸다.
        // Added.by.KWShin.2023.07.08.
        bufferNotNeeded: typeof request.handler.buffer != 'function',
        // 초기 BufferData 요청 시 화면에서 필요한 데이터 건수 지정.
        // Added.by.KWShin.2024.03.06.
        bufferSize: request.bufferSize,
        // 수집기가 보낸 데이터를 그대로 전달하는 것이 아니라
        // ViewServer에서 데이터를 가공하여 보내는 경우
        // 데이터 가공 시 필요한 필터링 조건을 보낸다.
        // Added.by.KWShin.2024.07
        filters: request.filters,
        lang: this.language,
        tz: this.timeZoneId,
      })),
    );
    if (this.getState() == window.WebSocket.OPEN) {
      this.instance.send(message);
    } else {
      this.queue.push(message);
    }
  }
  /**
   * 서버에서 데이터를 필터링하는 구독의 경우,
   * 화면에서 필터링 조건 설정이 변경되면
   * 서버에게 그 사실을 알린다.
   * Added.by.KWShin.2024.01.25.
   */
  updateFilter(args) {
    const message = JSON.stringify(
      args.requests.map(request => ({
        action: 'update-filter',
        key: request.key,
        filters: request.filters,
      })),
    );
    if (this.getState() == window.WebSocket.OPEN) {
      this.instance.send(message);
    } else {
      this.queue.push(message);
    }
    /*
     * 필터링 조건이 바뀌면 서버에서 버퍼 데이터를 다시 한번 내려준다.
     * 버퍼 데이터를 처리할 수 있도록 buffered를 true로 바꾸어 준다.
     * Added.by.KWShin.2024.01.25.
     */
    args.requests.forEach(req => {
      this.subscription.forEach(sub => {
        if (this.keyString(req.key) == this.keyString(sub.key)) {
          sub.buffered = true;
        }
      });
    });
  }
  getState() {
    const instance = this.instance || {};
    return instance.readyState === undefined ? -1 : instance.readyState;
  }
  close() {
    if (this.instance) {
      this.instance.close();
      this.state = WebSocketState.CLOSE;
    }
  }
}

export default {
  install(Vue, { store }) {
    const url = new URL(location.href);
    const wsp = 'http:' === url.protocol ? 'ws' : 'wss';
    Vue.prototype.$websocket = {
      alert: new WebSocket(store, `${wsp}://${new URL(location.href).host}/api/ws/alert`),
      realtime: new WebSocket(store, `${wsp}://${new URL(location.href).host}/api/ws/realtime`),
    };
  },
};
